/**
 * Optimized Style/Color Selectors — Mouth Tape + Nasal Strips
 *
 * Adds swatch images to the Style selector (Mouth Tape) / Color selector
 * (Nasal Strips), and a monthly price under each Quantity option in the
 * desktop fieldset (optionName-quantity). Reuses the pre-rendered
 * `.individual-element-quantity[data-option1/2/3][data-price]` markup
 * (already emitted per variant for the Loop mobile UI) as the price source,
 * matching the current selection so the right variant is priced.
 */
(() => {
    const path = window.location.pathname;

    const isMouthTape = /\/products\/dream-tape-mouth-tape(\/|$)/.test(path);
    const isNasalStrips = /\/products\/second-wind-nasal-strips(\/|$)/.test(path);
    if (!isMouthTape && !isNasalStrips) return;

    // Strips and Plus + always use the same image, regardless of the selected Color.
    const STYLE_FIXED_IMAGES = {
        strips: "https://cdn.shopify.com/s/files/1/0667/2551/1425/files/Strips_Cloud1-sc.png?v=1772029641",
        plus: "https://cdn.shopify.com/s/files/1/0667/2551/1425/files/skincare3-pc.png?v=1772029996",
    };

    // Full is the only Style whose image varies with the selected Color.
    const FULL_STYLE_COLOR_IMAGES = {
        cloud: "https://cdn.shopify.com/s/files/1/0667/2551/1425/files/image14_0861b090-a155-4177-bf73-0285897173cd.png?v=1784118093",
        sky: "https://cdn.shopify.com/s/files/1/0667/2551/1425/files/Sky_Model_Look_copy-777406-653324.jpg?v=1737195146",
        hotpink: "https://cdn.shopify.com/s/files/1/0667/2551/1425/files/Pink_Model_Sleep_copy-359708-857055.jpg?v=1737195146",
        black: "https://cdn.shopify.com/s/files/1/0667/2551/1425/files/Dream_Full_Black_Hero_-_1080.jpg?v=1793194412",
    };
    
    const COLOR_IMAGES = {
        sand: "https://cdn.shopify.com/s/files/1/0667/2551/1425/files/image_9_3_1_daebe81b-a9de-4975-9db7-c2f325ebb249.png?v=1787778518",
        black: "https://cdn.shopify.com/s/files/1/0667/2551/1425/files/image_9_3_1_2_98f0739a-c8bf-4efe-b84f-b31023cdb2ba.png?v=1787778518",
        icepack: "https://cdn.shopify.com/s/files/1/0667/2551/1425/files/image_9_3_1_3_43920610-fcde-4bfe-8d39-7e072a42cda2.png?v=1789487515",
        pink: "https://cdn.shopify.com/s/files/1/0667/2551/1425/files/image_9_3_1_4_1636614c-c68c-4e8f-a44c-aa8a8b88c5cd.png?v=1788294767",
        royalblue: "https://cdn.shopify.com/s/files/1/0667/2551/1425/files/image_13.png?v=1790338269",
    };

    const QUANTITY_BADGES = {
        "3month": { modifierClass: "dr-quantity-selector__badge--popular", text: "Most Popular" },
        "1year": { modifierClass: "dr-quantity-selector__badge--best-value", text: "Best Value" },
    };

    function normalize(value) {
        return (value || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
    }

    function decorateVisualLabel(label) {
        if (label.classList.contains("dr-visual-selector")) {
            return label.querySelector(".dr-visual-selector__image");
        }

        const name = label.dataset.value || label.textContent.trim();
        label.textContent = "";
        label.classList.add("dr-visual-selector");

        const image = document.createElement("span");
        image.className = "dr-visual-selector__image";
        image.setAttribute("aria-hidden", "true");

        const nameEl = document.createElement("span");
        nameEl.className = "dr-visual-selector__name";
        nameEl.textContent = name;

        label.append(image, nameEl);
        return image;
    }

    function decorateQuantityLabel(label) {
        if (label.classList.contains("dr-quantity-selector")) {
            return label.querySelector(".dr-quantity-selector__price");
        }

        const name = label.dataset.value || label.textContent.trim();
        label.textContent = "";
        label.classList.add("dr-quantity-selector");

        const badgeConfig = QUANTITY_BADGES[normalize(name)];
        if (badgeConfig) {
            const badge = document.createElement("span");
            badge.className = `dr-quantity-selector__badge ${badgeConfig.modifierClass}`;
            badge.textContent = badgeConfig.text;
            label.appendChild(badge);
        }

        const nameEl = document.createElement("span");
        nameEl.className = "dr-quantity-selector__name";
        nameEl.textContent = name;

        const priceEl = document.createElement("span");
        priceEl.className = "dr-quantity-selector__price";

        label.append(nameEl, priceEl);
        return priceEl;
    }

    function parseMoney(text) {
        const cleaned = (text || "").replace(/[^\d.,]/g, "");
        if (!cleaned) return NaN;

        const lastComma = cleaned.lastIndexOf(",");
        const lastDot = cleaned.lastIndexOf(".");
        const normalized = lastComma > lastDot
            ? cleaned.replace(/\./g, "").replace(",", ".")
            : cleaned.replace(/,/g, "");

        return parseFloat(normalized);
    }

    function formatDividedPrice(rawPriceText, divisor) {
        const match = (rawPriceText || "").match(/^([^\d]*)([\d.,]+)([^\d]*)$/);
        if (!match) return null;

        const [, prefix, numeric, suffix] = match;
        const amount = parseMoney(numeric);
        if (Number.isNaN(amount)) return null;

        const symbol = prefix || (suffix ? "" : "$");
        return `${symbol}${(amount / divisor).toFixed(2)}${suffix}`;
    }

    // "3 Month" -> divide by 3, "1 Year" -> divide by 12, "1 Month" -> divide by 1 (full price).
    function monthlyDivisor(quantityValue) {
        const match = (quantityValue || "").match(/(\d+)\s*(month|year)/i);
        if (!match) return null;

        const count = parseInt(match[1], 10);
        return match[2].toLowerCase() === "year" ? count * 12 : count;
    }

    function fieldsetPosition(allFieldsets, className) {
        return allFieldsets.findIndex((fieldset) => fieldset.classList.contains(className)) + 1;
    }

    function isSubscriptionSelected() {
        return Boolean(
            document.querySelector(".container-widget-subscription")?.classList.contains("selected-subscription")
        );
    }

    function findVariantPriceText(variantRadios, options, quantityPosition, quantityValue, isSubscribed) {
        const values = options.slice();
        values[quantityPosition - 1] = quantityValue;

        const selector = values
            .map((value, index) => `[data-option${index + 1}="${(value || "").replace(/"/g, '\\"')}"]`)
            .join("");

        const el = variantRadios.querySelector(`.individual-element-quantity${selector}`);
        if (!el) return null;

        if (isSubscribed && el.dataset.discountPrice) {
            return el.dataset.discountPrice;
        }

        return el.dataset.price || null;
    }

    function setup(variantRadios, styleFieldset, colorFieldset) {
        const allFieldsets = Array.from(variantRadios.querySelectorAll(":scope > fieldset"));
        const quantityFieldset = variantRadios.querySelector(".optionName-quantity");
        const quantityPosition = fieldsetPosition(allFieldsets, "optionName-quantity");

        function getCurrentOptions() {
            return allFieldsets.map((fieldset) => fieldset.querySelector("input:checked")?.value || "");
        }

        function updateStyleImages() {
            if (!styleFieldset || !colorFieldset) return;

            const currentColor = colorFieldset.querySelector("input:checked")?.value || "";

            styleFieldset.querySelectorAll("label.click-variant").forEach((label) => {
                const image = decorateVisualLabel(label);
                if (!image) return;

                const styleKey = normalize(label.dataset.value);
                const imageUrl = styleKey === "full"
                    ? FULL_STYLE_COLOR_IMAGES[normalize(currentColor)]
                    : STYLE_FIXED_IMAGES[styleKey];

                image.style.background = imageUrl
                    ? `url("${imageUrl}") lightgray 50% / cover no-repeat`
                    : "";
            });
        }

        function updateColorImages() {
            if (!colorFieldset || styleFieldset) return;

            colorFieldset.querySelectorAll("label.click-variant").forEach((label) => {
                const image = decorateVisualLabel(label);
                if (!image) return;

                const imageUrl = COLOR_IMAGES[normalize(label.dataset.value)];

                image.style.background = imageUrl
                    ? `url("${imageUrl}") lightgray 50% / cover no-repeat`
                    : "";
            });
        }

        function updateQuantityPrices() {
            if (!quantityFieldset || quantityPosition < 1) return;

            const currentOptions = getCurrentOptions();
            const isSubscribed = isSubscriptionSelected();

            quantityFieldset.querySelectorAll("label.click-variant").forEach((label) => {
                const priceEl = decorateQuantityLabel(label);
                if (!priceEl) return;

                const quantityValue = label.dataset.value || "";
                const divisor = monthlyDivisor(quantityValue);
                if (!divisor) {
                    priceEl.textContent = "";
                    return;
                }

                const priceText = findVariantPriceText(
                    variantRadios,
                    currentOptions,
                    quantityPosition,
                    quantityValue,
                    isSubscribed
                );
                const dividedPrice = formatDividedPrice(priceText, divisor);
                priceEl.textContent = dividedPrice ? `${dividedPrice} / mo` : "";
            });
        }

        function refresh() {
            updateStyleImages();
            updateColorImages();
            updateQuantityPrices();
        }

        refresh();
        variantRadios.addEventListener("change", refresh);

        // The Subscribe/One-time toggle lives outside <variant-radios> (it's the Loop
        // Subscriptions widget), so it never fires a "change" event here. Watch its
        // state class directly so the quantity prices stay in sync with it.
        const subscriptionContainer = document.querySelector(".container-widget-subscription");
        const subscriptionObserver = subscriptionContainer
            ? new MutationObserver(updateQuantityPrices)
            : null;
        subscriptionObserver?.observe(subscriptionContainer, { attributes: true, attributeFilter: ["class"] });
    }

    const anchorFieldset = document.querySelector(
        isMouthTape ? "fieldset.optionName-style" : "fieldset.optionName-color"
    );
    const variantRadios = anchorFieldset?.closest("variant-radios");
    if (!variantRadios) return;

    // Class on <html> scopes the global rules in optimized-selectors.css to these two products
    document.documentElement.classList.add("dr-price-large-hidden");

    const styleFieldset = isMouthTape ? variantRadios.querySelector(".optionName-style") : null;
    const colorFieldset = variantRadios.querySelector(".optionName-color");

    setup(variantRadios, styleFieldset, colorFieldset);
})();
